import { referencePrecipitation } from '../sim/precipitation.ts';
import { fair as presetFair, storm as presetStorm } from '../sim/presets.ts';
import type { StormBudget } from '../sim/storm.ts';
import { snowFraction, temperatureAt } from '../sim/temperature.ts';
import {
  totalHours,
  type FairTimelineEvent,
  type StormTimelineEvent,
  type TimelineEvent,
} from '../sim/timeline.ts';
import { conditions, selectedTimelineEventId, snowpack } from '../state/store.ts';
import {
  formatCompass16,
  formatDaysHours,
  formatEventSummary,
  formatNumber,
  formatSigned,
} from './format.ts';
import { NumberField } from './NumberField.tsx';
import { PresetPicker } from './PresetPicker.tsx';
import { TemperatureFields } from './TemperatureFields.tsx';
import { WindCompassRose } from './WindCompassRose.tsx';

let dupCounter = 0;
/** A fresh id for a duplicated event — the preset factories mint their own, but duplicating an
 * arbitrary existing event needs one here instead. */
function dupId(kind: TimelineEvent['kind']): string {
  return `${kind}-dup-${Date.now()}-${++dupCounter}`;
}

function tonnes(kg: number): number {
  return kg / 1000;
}

/** Weather totals up to the evaluation hour: snowfall, wind change, rain and the mass budget,
 * summed over every storm (and the aging/runoff of every event) so far — not just the selected
 * one. */
function WeatherBudgetSection({ budget }: { budget: StormBudget }) {
  const balanceKg = budget.depositedKg - budget.erodedKg - (budget.inflowKg - budget.outflowKg);
  const scale = Math.max(
    1,
    budget.depositedKg + budget.erodedKg + budget.inflowKg + budget.outflowKg,
  );
  const relativeError = Math.abs(balanceKg) / scale;
  return (
    <section class="panel-section">
      <h2>Weather budget</h2>
      <dl class="info-list">
        <dt>Snowfall</dt>
        <dd>{formatNumber(tonnes(budget.snowfallKg), 1)} t</dd>
        <dt>Rain</dt>
        <dd>{formatNumber(tonnes(budget.rainKg), 1)} t</dd>
        <dt>Shed (steep faces)</dt>
        <dd>{formatNumber(tonnes(budget.shedKg), 1)} t</dd>
        <dt>Eroded</dt>
        <dd>{formatNumber(tonnes(budget.erodedKg), 1)} t</dd>
        <dt>Deposited</dt>
        <dd>{formatNumber(tonnes(budget.depositedKg), 1)} t</dd>
        <dt>Inflow</dt>
        <dd>{formatNumber(tonnes(budget.inflowKg), 1)} t</dd>
        <dt>Outflow</dt>
        <dd>{formatNumber(tonnes(budget.outflowKg), 1)} t</dd>
        <dt>Runoff</dt>
        <dd>{formatNumber(tonnes(budget.runoffKg), 1)} t</dd>
        <dt>Mass-balance check</dt>
        <dd>
          {formatSigned(tonnes(balanceKg), 2)} t ({formatNumber(relativeError * 100, 2)}%)
        </dd>
      </dl>
      {!budget.allConverged ? (
        <p class="warning">
          Drift solver did not fully converge (up to {budget.maxSweeps} sweeps) — results may be
          slightly off mass balance.
        </p>
      ) : null}
    </section>
  );
}

/** Storm-only fields: new snow, reference elevation/gradient, wind, inflow. */
function StormFields({
  event,
  onChange,
}: {
  event: StormTimelineEvent;
  onChange: (patch: Partial<StormTimelineEvent>) => void;
}) {
  const refPrecip = referencePrecipitation(event, event.temperature, event.windSpeed);
  const refTemp = temperatureAt(event.temperature, event.referenceElevation);
  const refMixed = snowFraction(refTemp) < 1;
  return (
    <>
      <NumberField
        label="New snow"
        value={event.newSnowCm}
        min={0}
        max={200}
        step={1}
        unit="cm"
        onChange={(v) => onChange({ newSnowCm: v })}
      />
      <NumberField
        label="Reference elevation"
        value={event.referenceElevation}
        min={0}
        max={2500}
        step={10}
        unit="m"
        onChange={(v) => onChange({ referenceElevation: v })}
      />
      <NumberField
        label="Orographic gradient"
        value={event.orographicPctPer100m}
        min={0}
        max={20}
        step={0.5}
        unit="%/100 m"
        decimals={1}
        onChange={(v) => onChange({ orographicPctPer100m: v })}
      />
      <dl class="info-list">
        <dt>Precip. at reference elev.</dt>
        <dd>{formatNumber(refPrecip, 1)} mm</dd>
      </dl>
      {refMixed ? <p class="warning">Reference elevation gets mixed precipitation/rain.</p> : null}
      <NumberField
        label="Wind speed"
        value={event.windSpeed}
        min={0}
        max={30}
        step={0.5}
        unit="m/s"
        decimals={1}
        onChange={(v) => onChange({ windSpeed: v })}
      />
      <div class="wind-rose">
        <WindCompassRose deg={event.windFromDeg} onChange={(v) => onChange({ windFromDeg: v })} />
        <div class="wind-rose-info">
          <span class="compass-name">{formatCompass16(event.windFromDeg)}</span>
          <NumberField
            label="Wind from"
            value={event.windFromDeg}
            min={0}
            max={359}
            step={1}
            unit="° true"
            onChange={(v) => onChange({ windFromDeg: ((v % 360) + 360) % 360 })}
          />
        </div>
      </div>
      <div class="field-row">
        <span class="field-label">Upwind inflow</span>
        <select
          value={event.inflow}
          onChange={(e) =>
            onChange({
              inflow: (e.target as HTMLSelectElement).value as StormTimelineEvent['inflow'],
            })
          }
        >
          <option value="equilibrium">Snow blows in from outside (equilibrium)</option>
          <option value="none">None</option>
        </select>
      </div>
    </>
  );
}

/** Fair-weather-only field: clear sky (surface hoar + near-surface facets). */
function FairFields({
  event,
  onChange,
}: {
  event: FairTimelineEvent;
  onChange: (patch: Partial<FairTimelineEvent>) => void;
}) {
  return (
    <label class="checkbox-field">
      <input
        type="checkbox"
        checked={event.clearSky}
        onChange={(e) => onChange({ clearSky: (e.target as HTMLInputElement).checked })}
      />
      Clear sky (surface hoar &amp; near-surface facets)
    </label>
  );
}

/** Expanded editor for the selected event: duration, temperature and kind-specific fields, plus
 * reorder/duplicate/delete actions. */
function EventEditor({
  event,
  index,
  count,
  onChange,
  onDuplicate,
  onDelete,
  onMove,
}: {
  event: TimelineEvent;
  index: number;
  count: number;
  onChange: (next: TimelineEvent) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onMove: (dir: -1 | 1) => void;
}) {
  return (
    <div class="timeline-event-editor">
      <div class="timeline-event-actions">
        <button
          type="button"
          class="icon-button"
          disabled={index === 0}
          onClick={() => onMove(-1)}
          aria-label="Move up"
        >
          ↑
        </button>
        <button
          type="button"
          class="icon-button"
          disabled={index === count - 1}
          onClick={() => onMove(1)}
          aria-label="Move down"
        >
          ↓
        </button>
        <button type="button" class="icon-button" onClick={onDuplicate}>
          Duplicate
        </button>
        <button type="button" class="icon-button" disabled={count <= 1} onClick={onDelete}>
          Delete
        </button>
      </div>
      <NumberField
        label="Duration"
        value={event.durationHours}
        min={1}
        max={500}
        step={1}
        unit="h"
        onChange={(v) => onChange({ ...event, durationHours: v })}
      />
      <TemperatureFields
        value={event.temperature}
        onChange={(patch) =>
          onChange({ ...event, temperature: { ...event.temperature, ...patch } })
        }
      />
      {event.kind === 'storm' ? (
        <StormFields event={event} onChange={(patch) => onChange({ ...event, ...patch })} />
      ) : (
        <FairFields event={event} onChange={(patch) => onChange({ ...event, ...patch })} />
      )}
    </div>
  );
}

/**
 * Advanced mode's timeline editor: a preset picker, the event list (add/duplicate/delete/
 * reorder, one expanded for editing) and the weather-totals budget. Replaces the old single-storm
 * `StormPanel` now that weather is a sequence of events (see sim/timeline.ts).
 */
export function TimelineEditor() {
  const c = conditions.value;
  const events = c.events;
  const total = totalHours(events);
  const selected = selectedTimelineEventId.value;
  const selectedId =
    selected && events.some((e) => e.id === selected) ? selected : (events[0]?.id ?? null);
  const sp = snowpack.value;

  function updateEvents(next: TimelineEvent[]): void {
    const nextTotal = totalHours(next);
    const evaluationHour = Math.min(Math.max(0, conditions.peek().evaluationHour), nextTotal);
    conditions.value = { ...conditions.value, events: next, evaluationHour };
  }

  function replaceEvent(next: TimelineEvent): void {
    updateEvents(conditions.peek().events.map((e) => (e.id === next.id ? next : e)));
  }

  function addStorm(): void {
    const cur = conditions.peek();
    const e = presetStorm(24, 30, 12, 270, cur.temperature);
    updateEvents([...cur.events, e]);
    selectedTimelineEventId.value = e.id;
  }

  function addFair(): void {
    const cur = conditions.peek();
    const e = presetFair(24, false, cur.temperature);
    updateEvents([...cur.events, e]);
    selectedTimelineEventId.value = e.id;
  }

  function duplicateEvent(id: string): void {
    const cur = conditions.peek();
    const idx = cur.events.findIndex((e) => e.id === id);
    if (idx < 0) return;
    const copy = { ...cur.events[idx]!, id: dupId(cur.events[idx]!.kind) };
    updateEvents([...cur.events.slice(0, idx + 1), copy, ...cur.events.slice(idx + 1)]);
    selectedTimelineEventId.value = copy.id;
  }

  function deleteEvent(id: string): void {
    const cur = conditions.peek();
    if (cur.events.length <= 1) return;
    const idx = cur.events.findIndex((e) => e.id === id);
    const next = cur.events.filter((e) => e.id !== id);
    updateEvents(next);
    selectedTimelineEventId.value = next[Math.min(idx, next.length - 1)]?.id ?? null;
  }

  function moveEvent(id: string, dir: -1 | 1): void {
    const cur = conditions.peek();
    const idx = cur.events.findIndex((e) => e.id === id);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= cur.events.length) return;
    const next = cur.events.slice();
    [next[idx], next[j]] = [next[j]!, next[idx]!];
    updateEvents(next);
  }

  return (
    <>
      <PresetPicker />

      <section class="panel-section">
        <h2>Timeline</h2>
        <div class="timeline-event-list">
          {events.map((e, i) => (
            <div key={e.id}>
              <button
                type="button"
                class={'timeline-event-row' + (e.id === selectedId ? ' active' : '')}
                onClick={() => (selectedTimelineEventId.value = e.id)}
              >
                <span class="timeline-event-row-kind">{e.kind === 'storm' ? 'Storm' : 'Fair'}</span>
                <span class="timeline-event-row-summary">{formatEventSummary(e)}</span>
                <span class="timeline-event-row-duration">{formatDaysHours(e.durationHours)}</span>
              </button>
              {e.id === selectedId ? (
                <EventEditor
                  event={e}
                  index={i}
                  count={events.length}
                  onChange={replaceEvent}
                  onDuplicate={() => duplicateEvent(e.id)}
                  onDelete={() => deleteEvent(e.id)}
                  onMove={(dir) => moveEvent(e.id, dir)}
                />
              ) : null}
            </div>
          ))}
        </div>
        <div class="timeline-add-buttons">
          <button type="button" class="icon-button" onClick={addStorm}>
            + Storm
          </button>
          <button type="button" class="icon-button" onClick={addFair}>
            + Fair
          </button>
        </div>
        <p class="hint">Total: {formatDaysHours(total)}</p>
      </section>

      {sp?.storm ? <WeatherBudgetSection budget={sp.storm.budget} /> : null}
    </>
  );
}
